/**
 * Phase 197.b — Trend AI Longitudinal Growth Trajectory Card
 *
 * Cinematic dark-tile that surfaces the multi-period growth narrative
 * generated by Gemini 3 Flash across all of a student's progress cards.
 * Pulls from `GET /api/students/{id}/trend-trajectory`.
 *
 * Visibility per Phase 197.b spec:
 *   • Renders for students viewing themselves
 *   • Renders for parents viewing their child
 *   • Renders for coaches/owners/super_admin viewing any tenant student
 *
 * States:
 *   • loading       → soft skeleton
 *   • accumulating  → dashed empty-state showing progress toward 3-card unlock
 *   • ready         → full trajectory card with title, narrative, velocity gain,
 *                     plateau watch-area, coach endorsement line, and a
 *                     per-metric delta visualization rail
 *   • error         → silent (logged); the page stays clean
 *
 * Staff (academy_owner / coach / super_admin) see a "Regenerate" pill that
 * forces a fresh Gemini call via POST /trend-trajectory/refresh.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Sparkles, Activity, RefreshCw, Loader2,
  Award, LineChart, Heart,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STAFF_ROLES = new Set(['academy_owner', 'coach', 'super_admin']);

const formatDelta = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}`;
};

const prettifyMetric = (key) =>
  String(key || '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

export default function AthleteTrajectoryCard({ studentId }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, data: null });
  const [regenerating, setRegenerating] = useState(false);

  const canRegenerate = STAFF_ROLES.has(user?.role);

  const fetchTrajectory = useCallback(async () => {
    if (!studentId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(
        `/students/${encodeURIComponent(studentId)}/trend-trajectory`,
      );
      setState({ loading: false, data: r.data });
    } catch (_e) {
      setState({ loading: false, data: null });
    }
  }, [studentId]);

  const handleRegenerate = async () => {
    if (!studentId) return;
    setRegenerating(true);
    try {
      const r = await api.post(
        `/students/${encodeURIComponent(studentId)}/trend-trajectory/refresh`,
      );
      setState({ loading: false, data: r.data });
    } catch (_e) {
      // Silently leave the existing data in place
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => { fetchTrajectory(); }, [fetchTrajectory]);

  if (state.loading) {
    return (
      <div
        className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 animate-pulse"
        data-testid="trend-ai-skeleton"
      >
        <div className="h-3 w-32 bg-slate-800 rounded mb-3"></div>
        <div className="h-6 w-56 bg-slate-800 rounded mb-4"></div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-slate-800/70 rounded"></div>
          <div className="h-3 w-11/12 bg-slate-800/70 rounded"></div>
          <div className="h-3 w-9/12 bg-slate-800/70 rounded"></div>
        </div>
      </div>
    );
  }

  const data = state.data;
  if (!data) return null;

  // Accumulating state — friendly progress toward unlock threshold
  if (data.status === 'accumulating') {
    const have = data.cards_count || 0;
    const need = data.required || 3;
    const pct = Math.min(100, Math.round((have / need) * 100));
    return (
      <div
        className="bg-slate-900/30 border border-slate-800/80 border-dashed rounded-2xl p-5 text-center"
        data-testid="trend-ai-accumulating"
      >
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-3">
          <LineChart className="w-5 h-5 text-indigo-400" />
        </div>
        <h4 className="text-sm font-black text-slate-100 mb-1">
          Trend AI Locks In At {need} Cards
        </h4>
        <p className="text-[11px] text-slate-400 leading-relaxed max-w-sm mx-auto">
          {data.message || `Currently tracking ${have}/${need} progress cards.`}
        </p>
        <div className="mt-4 max-w-xs mx-auto">
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
              style={{ width: `${pct}%` }}
              data-testid="trend-ai-progress-bar"
            />
          </div>
          <div className="text-[10px] text-slate-500 mt-1.5 font-bold tabular-nums">
            {have}/{need} cards · {pct}%
          </div>
        </div>
      </div>
    );
  }

  if (data.status !== 'ready' || !data.trajectory) return null;

  const { trajectory, deltas = {}, cards_analyzed, source } = data;
  // Sort per-metric deltas: biggest gains first, then losses
  const metricRows = Object.entries(deltas.per_metric || {})
    .map(([k, v]) => ({ key: k, ...v }))
    .sort((a, b) => b.delta - a.delta);

  return (
    <div
      className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-950 border border-slate-800/80 rounded-2xl p-5 sm:p-6 text-white shadow-xl relative overflow-hidden"
      data-testid="trend-ai-card"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20 text-indigo-400 flex-shrink-0">
            <TrendingUp className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-[10px] font-black uppercase tracking-[0.18em] text-indigo-300/90">
                Trend AI · Longitudinal Analytics
              </h4>
              <span className="text-[9px] font-black uppercase bg-indigo-500/10 text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-500/20 tracking-wider">
                {cards_analyzed} cycles
              </span>
            </div>
            <h3
              className="text-base sm:text-lg font-black text-white tracking-tight mt-0.5 leading-snug"
              data-testid="trend-ai-title"
            >
              {trajectory.growth_trajectory_title}
            </h3>
          </div>
        </div>
        {canRegenerate && (
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 px-2 py-1 rounded-md transition-colors disabled:opacity-50"
            data-testid="trend-ai-regenerate"
          >
            {regenerating
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCw className="w-3 h-3" />}
            Regenerate
          </button>
        )}
      </div>

      {/* Narrative */}
      <p
        className="relative text-[13px] sm:text-sm text-slate-200 font-medium leading-relaxed bg-slate-900/40 border border-slate-800 p-4 rounded-xl mb-3"
        data-testid="trend-ai-narrative"
      >
        {trajectory.analytical_narrative}
      </p>

      {/* Velocity gain + plateau watch */}
      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <div
          className="flex items-start gap-2 bg-emerald-500/5 border border-emerald-500/15 p-3 rounded-xl"
          data-testid="trend-ai-velocity-gain"
        >
          <Sparkles className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-[0.15em] text-emerald-400">
              Steepest Ascent
            </div>
            <div className="text-[11px] sm:text-xs font-bold text-slate-100 mt-0.5">
              {trajectory.key_velocity_gain || '—'}
            </div>
          </div>
        </div>
        {trajectory.plateau_or_watch_area ? (
          <div
            className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/15 p-3 rounded-xl"
            data-testid="trend-ai-plateau"
          >
            <Activity className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-[9px] font-black uppercase tracking-[0.15em] text-amber-400">
                Watch Next
              </div>
              <div className="text-[11px] sm:text-xs font-bold text-slate-100 mt-0.5">
                {trajectory.plateau_or_watch_area}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 bg-slate-800/40 border border-slate-800 p-3 rounded-xl">
            <Award className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">
                Form Check
              </div>
              <div className="text-[11px] sm:text-xs font-bold text-slate-100 mt-0.5">
                No regressions detected
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Per-metric delta rail */}
      {metricRows.length > 0 && (
        <div
          className="relative bg-slate-900/40 border border-slate-800 p-3 rounded-xl mb-3"
          data-testid="trend-ai-delta-rail"
        >
          <div className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400 mb-2">
            Per-Skill Movement (First → Latest)
          </div>
          <div className="space-y-1.5">
            {metricRows.slice(0, 6).map((row) => {
              const positive = row.delta > 0;
              const negative = row.delta < 0;
              const ToneIcon = positive ? TrendingUp : (negative ? TrendingDown : Activity);
              const toneClass = positive
                ? 'text-emerald-400'
                : (negative ? 'text-rose-400' : 'text-slate-400');
              return (
                <div key={row.key}
                     className="flex items-center justify-between gap-2 text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <ToneIcon className={`w-3 h-3 flex-shrink-0 ${toneClass}`} />
                    <span className="font-bold text-slate-200 truncate">
                      {prettifyMetric(row.key)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 font-bold tabular-nums">
                    <span className="text-slate-500">{row.first}/5</span>
                    <span className="text-slate-600">→</span>
                    <span className="text-slate-100">{row.last}/5</span>
                    <span className={`font-black ${toneClass} w-12 text-right`}>
                      {formatDelta(row.delta)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Coach endorsement line */}
      {trajectory.coach_endorsement_line && (
        <div
          className="relative flex items-start gap-2 text-[11px] text-slate-300 italic font-medium"
          data-testid="trend-ai-endorsement"
        >
          <Heart className="w-3.5 h-3.5 text-rose-400/80 flex-shrink-0 mt-0.5" />
          <span>{trajectory.coach_endorsement_line}</span>
        </div>
      )}

      {/* Footer tag */}
      <div className="relative mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[9px] text-slate-500 font-bold tracking-wider uppercase">
        <span>Phase 197.b · Gemini 3 Flash</span>
        <span>{source === 'llm' ? 'Live Generation' : 'Heuristic Fallback'}</span>
      </div>
    </div>
  );
}
